// src/store/modules/news.js - 画像アップロード完全対応版（キャッシュ回避修正済み）
import axios from 'axios';
import Papa from 'papaparse';

const state = {
    newsList: [],
    currentNews: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    uploadingImage: false
};

const mutations = {
    SET_NEWS_LIST(state, news) {
        state.newsList = news;
    },
    SET_CURRENT_NEWS(state, news) {
        state.currentNews = news;
    },
    SET_AUTHENTICATED(state, status) {
        state.isAuthenticated = status;
    },
    SET_LOADING(state, status) {
        state.loading = status;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_UPLOADING_IMAGE(state, status) {
        state.uploadingImage = status;
    },
    ADD_NEWS(state, news) {
        state.newsList.unshift(news);
    },
    UPDATE_NEWS(state, updatedNews) {
        const index = state.newsList.findIndex(n => n.id === updatedNews.id);
        if (index !== -1) {
            state.newsList.splice(index, 1, updatedNews);
        }
    },
    DELETE_NEWS(state, id) {
        state.newsList = state.newsList.filter(n => n.id !== id);
    }
};

// 画像をCloudinaryにアップロード（推奨）
async function uploadToCloudinary(base64Image) {
    try {
        const cloudName = process.env.VUE_APP_CLOUDINARY_CLOUD_NAME;
        const uploadPreset = process.env.VUE_APP_CLOUDINARY_UPLOAD_PRESET;
        
        if (!cloudName || !uploadPreset) {
            console.error('Cloudinary settings not configured');
            return null;
        }
        
        const formData = new FormData();
        formData.append('file', base64Image);
        formData.append('upload_preset', uploadPreset);
        
        const response = await fetch(
            `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
            {
                method: 'POST',
                body: formData
            }
        );
        
        const data = await response.json();
        if (data.secure_url) {
            return data.secure_url;
        }
        return null;
    } catch (error) {
        console.error('Error uploading to Cloudinary:', error);
        return null;
    }
}

// 画像をImgBBにアップロード
async function uploadToImgBB(base64Image) {
    try {
        const apiKey = process.env.VUE_APP_IMGBB_API_KEY;
        
        if (!apiKey) {
            console.error('ImgBB API key not configured');
            return null;
        }
        
        // Base64のヘッダー部分を削除
        const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, '');
        
        const formData = new FormData();
        formData.append('key', apiKey);
        formData.append('image', base64Data);
        
        const response = await fetch('https://api.imgbb.com/1/upload', {
            method: 'POST',
            body: formData
        });
        
        const data = await response.json();
        if (data.success && data.data.url) {
            return data.data.url;
        }
        return null;
    } catch (error) {
        console.error('Error uploading to ImgBB:', error);
        return null;
    }
}

// 画像をImgurにアップロード（代替案）
async function uploadToImgur(base64Image) {
    try {
        // Base64のヘッダー部分を削除
        const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, '');
        
        // Imgur Client ID
        const clientId = process.env.VUE_APP_IMGUR_CLIENT_ID;
        
        if (!clientId) {
            console.error('Imgur Client ID not configured');
            return null;
        }
        
        const formData = new FormData();
        formData.append('image', base64Data);
        formData.append('type', 'base64');
        
        const response = await fetch('https://api.imgur.com/3/image', {
            method: 'POST',
            headers: {
                'Authorization': `Client-ID ${clientId}`
            },
            body: formData
        });
        
        const data = await response.json();
        if (data.success && data.data.link) {
            return data.data.link;
        }
        return null;
    } catch (error) {
        console.error('Error uploading to Imgur:', error);
        return null;
    }
}

// 画像アップロードのメイン関数
async function uploadImage(base64Image) {
    // 1. Cloudinaryを試す（設定されている場合）
    if (process.env.VUE_APP_CLOUDINARY_CLOUD_NAME) {
        const url = await uploadToCloudinary(base64Image);
        if (url) return url;
    }
    
    // 2. ImgBBを試す（設定されている場合）
    if (process.env.VUE_APP_IMGBB_API_KEY) {
        const url = await uploadToImgBB(base64Image);
        if (url) return url;
    }
    
    // 3. Imgurを試す（設定されている場合）
    if (process.env.VUE_APP_IMGUR_CLIENT_ID) {
        const url = await uploadToImgur(base64Image);
        if (url) return url;
    }
    
    // 4. アップロードできない場合は、デフォルト画像を返す
    console.warn('No image upload service configured. Using default image.');
    return 'https://www.gharb.tokyo/assets/img/blog/news1.png';
}

const actions = {
    // 認証チェック
    authenticate({ commit }, password) {
        const adminPassword = process.env.VUE_APP_NEWS_ADMIN_PASSWORD || '0719';
        if (password === adminPassword) {
            commit('SET_AUTHENTICATED', true);
            sessionStorage.setItem('newsAdminAuth', 'true');
            return true;
        }
        return false;
    },

    // 認証状態の確認
    checkAuth({ commit }) {
        const isAuth = sessionStorage.getItem('newsAdminAuth') === 'true';
        commit('SET_AUTHENTICATED', isAuth);
        return isAuth;
    },

    // ログアウト
    logout({ commit }) {
        commit('SET_AUTHENTICATED', false);
        sessionStorage.removeItem('newsAdminAuth');
    },

    // ニュース一覧を取得（Google Sheetsから）
    async fetchNewsList({ commit }) {
        commit('SET_LOADING', true);
        try {
            const SHEET_ID = process.env.VUE_APP_GOOGLE_SHEET_ID;
            if (!SHEET_ID) {
                console.log('No Google Sheet ID configured');
                commit('SET_NEWS_LIST', []);
                return;
            }

            // キャッシュ回避のためタイムスタンプを追加
            const CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv&gid=0&t=${Date.now()}`;
            const response = await fetch(CSV_URL);
            const csvText = await response.text();
            
            Papa.parse(csvText, {
                header: true,
                complete: (results) => {
                    const newsItems = results.data
                        .filter(row => row.id && row.title)
                        .map(row => {
                            // publishedフィールドの正規化（大文字・小文字・日本語対応）
                            let published = false;
                            const publishedValue = (row.published || '').toString().toUpperCase().trim();
                            if (publishedValue === 'TRUE' || publishedValue === '公開' || publishedValue === 'YES' || publishedValue === '1') {
                                published = true;
                            }
                            
                            return {
                                id: row.id,
                                title: row.title,
                                date: row.date,
                                content: row.content,
                                published: published,
                                image1: row.image1 || 'https://www.gharb.tokyo/assets/img/blog/news1.png',
                                image2: row.image2 || '',
                                image3: row.image3 || '',
                                image4: row.image4 || '',
                                image5: row.image5 || ''
                            };
                        })
                        .sort((a, b) => new Date(b.date) - new Date(a.date));
                    
                    commit('SET_NEWS_LIST', newsItems);
                }
            });
        } catch (error) {
            console.error('Error fetching news:', error);
            commit('SET_ERROR', error.message);
            commit('SET_NEWS_LIST', []);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // ニュースを作成
    async createNews({ commit, dispatch }, newsData) {
        try {
            commit('SET_UPLOADING_IMAGE', true);
            
            // 各画像をアップロード
            const processedData = { ...newsData };
            for (let i = 1; i <= 5; i++) {
                const fieldName = `image${i}`;
                if (processedData[fieldName] && processedData[fieldName].startsWith('data:image')) {
                    console.log(`Uploading image ${i}...`);
                    const uploadedUrl = await uploadImage(processedData[fieldName]);
                    processedData[fieldName] = uploadedUrl;
                    console.log(`Image ${i} uploaded:`, uploadedUrl);
                }
            }
            
            const newNews = {
                id: Date.now().toString(),
                ...processedData,
                image1: processedData.image1 || 'https://www.gharb.tokyo/assets/img/blog/news1.png',
                createdAt: new Date().toISOString()
            };

            // Google Apps Scriptエンドポイントにデータを送信
            const SCRIPT_URL = process.env.VUE_APP_GOOGLE_SCRIPT_URL;
            if (SCRIPT_URL) {
                try {
                    const response = await fetch(SCRIPT_URL, {
                        method: 'POST',
                        redirect: 'follow',
                        headers: {
                            'Content-Type': 'text/plain;charset=utf-8',
                        },
                        body: JSON.stringify({
                            action: 'create',
                            data: newNews
                        })
                    });
                    
                    console.log('Data sent to Google Sheets');
                } catch (error) {
                    console.error('Error sending to Google Apps Script:', error);
                }
            }
            
            commit('ADD_NEWS', newNews);
            
            // データを再取得（3秒後）
            setTimeout(() => {
                dispatch('fetchNewsList');
            }, 3000);
            
            return newNews;
        } catch (error) {
            console.error('Error creating news:', error);
            throw error;
        } finally {
            commit('SET_UPLOADING_IMAGE', false);
        }
    },

    // ニュースを更新
    async updateNews({ commit, dispatch }, { id, newsData }) {
        try {
            commit('SET_UPLOADING_IMAGE', true);
            
            // 各画像をアップロード
            const processedData = { ...newsData };
            for (let i = 1; i <= 5; i++) {
                const fieldName = `image${i}`;
                if (processedData[fieldName] && processedData[fieldName].startsWith('data:image')) {
                    console.log(`Uploading image ${i}...`);
                    const uploadedUrl = await uploadImage(processedData[fieldName]);
                    processedData[fieldName] = uploadedUrl;
                }
            }
            
            const updatedNews = {
                ...processedData,
                id,
                updatedAt: new Date().toISOString()
            };

            // Google Apps Scriptエンドポイントにデータを送信
            const SCRIPT_URL = process.env.VUE_APP_GOOGLE_SCRIPT_URL;
            if (SCRIPT_URL) {
                try {
                    await fetch(SCRIPT_URL, {
                        method: 'POST',
                        redirect: 'follow',
                        headers: {
                            'Content-Type': 'text/plain;charset=utf-8',
                        },
                        body: JSON.stringify({
                            action: 'update',
                            id: id,
                            data: updatedNews
                        })
                    });
                } catch (error) {
                    console.error('Error updating Google Sheets:', error);
                }
            }
            
            commit('UPDATE_NEWS', updatedNews);
            
            // データを再取得
            setTimeout(() => {
                dispatch('fetchNewsList');
            }, 3000);
            
            return updatedNews;
        } finally {
            commit('SET_UPLOADING_IMAGE', false);
        }
    },

    // ニュースを削除
    async deleteNews({ commit, dispatch }, id) {
        // Google Apps Scriptエンドポイントにデータを送信
        const SCRIPT_URL = process.env.VUE_APP_GOOGLE_SCRIPT_URL;
        if (SCRIPT_URL) {
            try {
                await fetch(SCRIPT_URL, {
                    method: 'POST',
                    redirect: 'follow',
                    headers: {
                        'Content-Type': 'text/plain;charset=utf-8',
                    },
                    body: JSON.stringify({
                        action: 'delete',
                        id: id
                    })
                });
            } catch (error) {
                console.error('Error deleting from Google Sheets:', error);
            }
        }
        
        commit('DELETE_NEWS', id);
        
        // データを再取得
        setTimeout(() => {
            dispatch('fetchNewsList');
        }, 3000);
    }
};

const getters = {
    publishedNews: state => {
        return state.newsList.filter(news => news.published === true);
    },
    draftNews: state => {
        return state.newsList.filter(news => news.published !== true);
    },
    getNewsById: state => id => state.newsList.find(news => news.id === id),
    isUploadingImage: state => state.uploadingImage
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};